import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MANIFESTO, BUSINESS_STORY, ABOUT_IMAGE } from "../data/business";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { MapSection } from "../components/MapSection";
import { CTASection } from "../components/CTASection";
import { useSEO } from "../hooks/useSEO";

export default function About() {
  useSEO(
    "About | New Lookz Hair Salon — New Bakaram, Gandhi Nagar",
    "A local hair salon in New Bakaram, Gandhi Nagar, Hyderabad — simple, comfortable hair and grooming focused on the look you want.",
    "/about"
  );

  return (
    <>
      <section className="bg-ink grain" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading
              dark
              eyebrow="About New Lookz"
              title="A Local Salon for Everyday Style"
              text="New Lookz Hair Salon is located in New Bakaram, Gandhi Nagar, serving customers from the surrounding Hyderabad area."
            />
            <Reveal delay={0.1}>
              <p className="mt-4 text-white/60 text-base leading-relaxed">
                We believe getting your hair done should be simple, comfortable and focused on the look
                you actually want. Whether you're maintaining your regular style or trying something
                different, New Lookz provides a convenient local place to take care of your hair and
                grooming needs.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="rounded-t-[8rem] rounded-b-xl overflow-hidden border border-copper/25 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]">
              <img src={ABOUT_IMAGE.src} alt={ABOUT_IMAGE.alt} width={1200} height={900} className="w-full aspect-[4/3] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="bg-ivory" data-testid="about-manifesto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHeading eyebrow="Our Philosophy" title="How We Think About the Chair" />
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.1} className="h-full">
                <div className="h-full rounded-xl bg-ink text-white p-8 relative overflow-hidden group">
                  <div className="absolute -bottom-10 -right-6 font-display font-extrabold text-[7rem] leading-none text-copper/10 group-hover:text-copper/20 transition-colors duration-500" aria-hidden="true">
                    {m.n}
                  </div>
                  <p className="font-editorial italic text-copper text-xl">{m.n}</p>
                  <h3 className="font-display font-bold text-2xl mt-3">{m.title}</h3>
                  <p className="mt-3 text-white/55 text-sm leading-relaxed relative">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper border-y border-ink/10" data-testid="about-story">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.28em] uppercase text-copper mb-4">Our Story</p>
            <blockquote className="font-editorial italic text-2xl sm:text-3xl text-body/85 leading-snug" data-testid="about-business-story">
              “{BUSINESS_STORY}”
            </blockquote>
            <p className="mt-6 text-mute text-sm">
              Founding history, team profiles and awards will be published here once verified by the business.
            </p>
            <Link to="/book" data-testid="about-book-link" className="group mt-8 inline-flex items-center gap-2 font-display font-bold text-sm text-ink border-b-2 border-copper pb-1 hover:text-copper transition-colors">
              Experience It Yourself <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <MapSection />
      <CTASection />
    </>
  );
}
